import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Card, CardContent, Chip, Button, Stack,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, IconButton,
  CircularProgress, Alert, useTheme, Avatar, List, ListItem, ListItemAvatar,
  ListItemText, Collapse, Tooltip, Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon, BugReport as BugIcon, AccountTree as CascadeIcon,
  History as HistoryIcon, CheckCircle as PassIcon, Warning as WarnIcon,
  Error as ErrorIcon, Refresh as RefreshIcon, Build as FixIcon,
  OpenInNew as OpenIcon, BrokenImage, Sort, PriceChange, MoneyOff,
  Verified, Visibility, ExpandMore, ExpandLess
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import OrphanBatchFixDialog from '../../components/dialogs/OrphanBatchFixDialog';
import FifoFixDialog from '../../components/dialogs/FifoFixDialog';
import DocumentPreviewModal from '../../components/dialogs/DocumentPreviewModal';

// ─── Types ──────────────────────────────────────────────────────────
interface SummaryCard { count: number; value?: number | null; label: string; labelAr: string; severity: string; subtitle?: string; }
interface Category { categoryKey: string; title: string; titleAr: string; icon: string; issueCount: number; financialImpact?: number | null; severity: string; fixAction?: string; canAutoFix: boolean; description?: string; descriptionAr?: string; }
interface RecentFix { at: string; fixType: string; fixTypeAr: string; description: string; descriptionAr: string; userName?: string | null; isSuccess: boolean; referenceId?: string | null; }
interface Overview { checkedAt: string; costingMethod: string; overallHealth: string; criticalIssues: SummaryCard; warningIssues: SummaryCard; passedChecks: SummaryCard; financialImpact: SummaryCard; categories: Category[]; recentFixes: RecentFix[]; }

interface TrackedIssue {
  issueId: string;
  issueType: string;
  issueTypeLabel: string;
  issueTypeLabelAr: string;
  severity: string;
  itemId?: number | null;
  itemCode?: string | null;
  itemName?: string | null;
  itemNameAr?: string | null;
  location?: string | null;
  locationAr?: string | null;
  movementsAffected: number;
  financialImpact?: number | null;
  summary: string;
  summaryAr: string;
  canAutoFix: boolean;
  fixActionType: string;
  cascadeRequest: any;
}
interface IssueTrackerResult { checkedAt: string; totalIssues: number; totalFinancialImpact: number; issues: TrackedIssue[]; }

interface CascadeNode { nodeId: string; nodeType: string; entityId: number; documentNumber: string; description?: string | null; descriptionAr?: string | null; date?: string | null; status?: string | null; amountBefore?: number | null; amountAfter?: number | null; delta?: number | null; severity: string; navigationPath?: string | null; requiresFix: boolean; fixHint?: string | null; fixHintAr?: string | null; }
interface CascadeStep { stepNumber: number; action: string; title: string; titleAr: string; description: string; descriptionAr: string; executionMode: string; navigationPath?: string | null; }
interface CascadeAnalysis { issueType: string; rootCauseDescription: string; rootCauseDescriptionAr: string; rootNode?: CascadeNode | null; affectedNodes: CascadeNode[]; recommendedSteps: CascadeStep[]; totalFinancialImpact?: number | null; canFullAutoFix: boolean; }

interface HistoryEntry { entryId: string; at: string; fixType: string; fixTypeAr: string; title: string; titleAr: string; description: string; descriptionAr: string; userName?: string | null; changeCount?: number | null; financialImpact?: number | null; status: string; canRollback: boolean; rollbackEndpoint?: string | null; }
interface HistoryResult { fetchedAt: string; totalEntries: number; entries: HistoryEntry[]; }

// ─── Helpers ────────────────────────────────────────────────────────
const fmt = (n: number | null | undefined, decimals = 4) => (n === null || n === undefined) ? '—' : n.toFixed(decimals);
const fmtDate = (d: string | null | undefined, isRTL: boolean) => !d ? '—' : new Date(d).toLocaleString(isRTL ? 'ar-KW' : 'en-GB');
const fmtDateOnly = (d: string | null | undefined, isRTL: boolean) => !d ? '—' : new Date(d).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');

const InventoryHealthHubPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [issues, setIssues] = useState<IssueTrackerResult | null>(null);
  const [history, setHistory] = useState<HistoryResult | null>(null);

  // expanded issue row + its cascade
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const [loadingCascade, setLoadingCascade] = useState(false);
  const [cascadeData, setCascadeData] = useState<Record<string, CascadeAnalysis>>({});

  const [orphanOpen, setOrphanOpen] = useState(false);
  const [fifoOpen, setFifoOpen] = useState(false);
  const [docModal, setDocModal] = useState<{ open: boolean; type: string | null; id: number | null }>({ open: false, type: null, id: null });

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    if (tab === 1 && !issues) loadIssues();
    if (tab === 2 && !history) loadHistory();
  }, [tab]);

  const loadOverview = async () => {
    setLoadingOverview(true);
    try {
      const r = await axiosInstance.get<Overview>('/InventoryHealthHub/overview');
      setOverview(r.data);
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed to load overview'); }
    finally { setLoadingOverview(false); }
  };
  const loadIssues = async () => {
    setLoadingIssues(true);
    try {
      const r = await axiosInstance.get<IssueTrackerResult>('/InventoryHealthHub/issues/all');
      setIssues(r.data);
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setLoadingIssues(false); }
  };
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await axiosInstance.get<HistoryResult>('/InventoryHealthHub/history?take=100');
      setHistory(r.data);
    } catch (e: any) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setLoadingHistory(false); }
  };

  const toggleExpand = async (issue: TrackedIssue) => {
    if (expandedIssueId === issue.issueId) {
      setExpandedIssueId(null);
      return;
    }
    setExpandedIssueId(issue.issueId);
    if (!cascadeData[issue.issueId]) {
      setLoadingCascade(true);
      try {
        const r = await axiosInstance.post<CascadeAnalysis>('/InventoryHealthHub/cascade/analyze', issue.cascadeRequest);
        setCascadeData(prev => ({ ...prev, [issue.issueId]: r.data }));
      } catch (e: any) {
        toast.error(e.response?.data?.message || 'Cascade load failed');
        setExpandedIssueId(null);
      } finally { setLoadingCascade(false); }
    }
  };

  const triggerFix = (issue: TrackedIssue) => {
    if (issue.fixActionType === 'OrphanFix') setOrphanOpen(true);
    else if (issue.fixActionType === 'FifoFix') setFifoOpen(true);
    else if (issue.fixActionType === 'Rebalance') navigate('/tools/inventory-rebalance');
    else toast.info(isRTL ? 'هذا الخلل يحتاج تدخل يدوي — راجع المستندات المنبع' : 'This requires manual intervention — review source documents');
  };

  const sevColor = (s: string) => s === 'critical' ? '#f44336' : s === 'warning' ? '#ff9800' : s === 'success' ? '#4caf50' : '#2196f3';
  const sevBg = (s: string) => s === 'critical' ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' : s === 'warning' ? 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)' : s === 'success' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)';
  const sevIcon = (s: string) => s === 'critical' ? <ErrorIcon /> : s === 'warning' ? <WarnIcon /> : s === 'success' ? <PassIcon /> : <DashboardIcon />;
  const catIcon = (k: string) => k === 'BrokenImage' ? <BrokenImage /> : k === 'Sort' ? <Sort /> : k === 'PriceChange' ? <PriceChange /> : k === 'MoneyOff' ? <MoneyOff /> : <BugIcon />;

  // ═════════ TAB 1: OVERVIEW ═════════════════════════════════════════
  const renderOverview = () => {
    if (loadingOverview) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!overview) return <Alert severity="info">{isRTL ? 'اضغط Refresh' : 'Click Refresh'}</Alert>;
    return (
      <Stack spacing={3}>
        <Paper sx={{
          p: 3, borderRadius: 3, color: 'white',
          background: overview.overallHealth === 'Healthy' ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                    : overview.overallHealth === 'Critical' ? 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)'
                    : 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)'
        }}>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
              {overview.overallHealth === 'Healthy' ? <PassIcon sx={{ fontSize: 32 }} /> : overview.overallHealth === 'Critical' ? <ErrorIcon sx={{ fontSize: 32 }} /> : <WarnIcon sx={{ fontSize: 32 }} />}
            </Avatar>
            <Box flex={1} minWidth={200}>
              <Typography variant="h4" fontWeight="bold">
                {isRTL ? (overview.overallHealth === 'Healthy' ? '🎉 النظام سليم' : overview.overallHealth === 'Critical' ? '🚨 يحتاج تدخل فوري' : '⚠️ تحذيرات نشطة') : (overview.overallHealth === 'Healthy' ? 'System Healthy' : overview.overallHealth === 'Critical' ? 'Critical Action Needed' : 'Active Warnings')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'سياسة التسعير:' : 'Costing:'} {overview.costingMethod} · {isRTL ? 'آخر فحص:' : 'Last:'} {fmtDate(overview.checkedAt, isRTL)}</Typography>
            </Box>
            <Button variant="contained" startIcon={<RefreshIcon />} onClick={loadOverview} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>{isRTL ? 'إعادة فحص' : 'Refresh'}</Button>
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          {[overview.criticalIssues, overview.warningIssues, overview.passedChecks, overview.financialImpact].map((card, i) => (
            <Card key={i} sx={{ background: sevBg(card.severity), color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? card.labelAr : card.label}</Typography>
                    <Typography variant="h3" fontWeight="bold">{card.count}</Typography>
                    {card.value !== undefined && card.value !== null && <Typography variant="caption">{fmt(card.value)} KWD</Typography>}
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>{card.subtitle}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.25)' }}>{sevIcon(card.severity)}</Avatar>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">{isRTL ? '📂 فئات الفحوصات' : 'Check Categories'}</Typography>
            <Button size="small" startIcon={<BugIcon />} onClick={() => setTab(1)} variant="outlined">{isRTL ? 'عرض كل المشاكل بالتفصيل ←' : 'View all issues in detail →'}</Button>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {overview.categories.map(cat => (
              <Paper key={cat.categoryKey} variant="outlined" sx={{ p: 2, borderRadius: 2, borderLeft: `4px solid ${sevColor(cat.severity)}` }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                  <Avatar sx={{ bgcolor: sevColor(cat.severity), width: 36, height: 36 }}>{catIcon(cat.icon)}</Avatar>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">{isRTL ? cat.titleAr : cat.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{isRTL ? cat.descriptionAr : cat.description}</Typography>
                  </Box>
                  <Chip label={cat.issueCount} color={cat.issueCount > 0 ? (cat.severity === 'critical' ? 'error' : 'warning') : 'success'} sx={{ minWidth: 50, fontWeight: 'bold' }} />
                </Box>
                {cat.issueCount > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    {cat.fixAction === 'OrphanFix' && <Button size="small" variant="contained" startIcon={<FixIcon />} onClick={() => setOrphanOpen(true)} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>{isRTL ? 'إصلاح' : 'Fix'}</Button>}
                    {cat.fixAction === 'FifoFix' && <Button size="small" variant="contained" startIcon={<FixIcon />} onClick={() => setFifoOpen(true)} sx={{ background: 'linear-gradient(135deg, #f12711 0%, #f5af19 100%)' }}>{isRTL ? 'إصلاح FIFO' : 'Fix FIFO'}</Button>}
                    {cat.fixAction === 'Rebalance' && <Button size="small" variant="outlined" startIcon={<OpenIcon />} onClick={() => navigate('/tools/inventory-rebalance')}>Rebalance</Button>}
                    <Button size="small" startIcon={<BugIcon />} onClick={() => setTab(1)}>{isRTL ? 'التفاصيل' : 'Details'}</Button>
                  </Stack>
                )}
              </Paper>
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>{isRTL ? '📜 آخر الإصلاحات' : 'Recent Fixes'}</Typography>
          {!overview.recentFixes || overview.recentFixes.length === 0 ? (
            <Alert severity="info">{isRTL ? 'لا توجد إصلاحات حديثة' : 'No recent fixes'}</Alert>
          ) : (
            <List dense>
              {overview.recentFixes.slice(0, 10).map((f, i) => (
                <ListItem key={i} divider>
                  <ListItemAvatar><Avatar sx={{ bgcolor: f.isSuccess ? '#4caf50' : '#f44336' }}>{f.isSuccess ? <PassIcon /> : <ErrorIcon />}</Avatar></ListItemAvatar>
                  <ListItemText
                    primary={<Box display="flex" alignItems="center" gap={1} flexWrap="wrap"><Chip label={isRTL ? f.fixTypeAr : f.fixType} size="small" /><span>{isRTL ? f.descriptionAr : f.description}</span></Box>}
                    secondary={`${fmtDate(f.at, isRTL)}${f.userName ? ` · ${f.userName}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Stack>
    );
  };

  // ═════════ TAB 2: ISSUE TRACKER (auto-detect, click to expand) ═════
  const renderIssueTracker = () => {
    if (loadingIssues) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!issues) return <Alert severity="info">{isRTL ? 'جارٍ التحميل...' : 'Loading...'}</Alert>;
    if (issues.totalIssues === 0) {
      return <Alert severity="success" icon={<PassIcon />} sx={{ fontSize: '1rem' }}>{isRTL ? '🎉 لا توجد أي مشاكل — النظام سليم تماماً' : '🎉 No issues — system is clean'}</Alert>;
    }
    return (
      <Stack spacing={2}>
        <Alert severity="info" icon={<BugIcon />}>
          {isRTL
            ? `وجدنا ${issues.totalIssues} مشكلة في النظام. اضغط على أي صف لرؤية شجرة التأثير + المستندات المتأثرة + خطوات الإصلاح.`
            : `Found ${issues.totalIssues} issues. Click any row to see impact tree + affected documents + fix steps.`}
          {issues.totalFinancialImpact !== 0 && (
            <Chip sx={{ ml: 2 }} label={`${isRTL ? 'إجمالي الأثر المالي:' : 'Total Financial Impact:'} ${fmt(issues.totalFinancialImpact)} KWD`} color="warning" size="small" />
          )}
        </Alert>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                  <TableCell sx={{ width: 40 }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'النوع' : 'Type'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الموقع' : 'Location'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'الخطورة' : 'Severity'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'متأثر' : 'Affected'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{isRTL ? 'الأثر (KWD)' : 'Impact'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'إصلاح' : 'Fix'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issues.issues.map(issue => {
                  const expanded = expandedIssueId === issue.issueId;
                  const cascade = cascadeData[issue.issueId];
                  return (
                    <React.Fragment key={issue.issueId}>
                      <TableRow hover sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }} onClick={() => toggleExpand(issue)}>
                        <TableCell><IconButton size="small">{expanded ? <ExpandLess /> : <ExpandMore />}</IconButton></TableCell>
                        <TableCell><Chip label={isRTL ? issue.issueTypeLabelAr : issue.issueTypeLabel} size="small" color={issue.severity === 'critical' ? 'error' : issue.severity === 'warning' ? 'warning' : 'info'} /></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{issue.itemCode || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{isRTL ? (issue.itemNameAr || issue.itemName || '—') : (issue.itemName || '—')}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{isRTL ? (issue.locationAr || issue.location || '—') : (issue.location || '—')}</Typography></TableCell>
                        <TableCell align="center"><Chip label={issue.severity} size="small" sx={{ bgcolor: sevColor(issue.severity), color: 'white', textTransform: 'capitalize' }} /></TableCell>
                        <TableCell align="center"><Chip label={issue.movementsAffected} size="small" /></TableCell>
                        <TableCell align="right" sx={{ color: '#d32f2f', fontWeight: issue.financialImpact ? 'bold' : 'normal' }}>{fmt(issue.financialImpact)}</TableCell>
                        <TableCell align="center">
                          {issue.canAutoFix ? (
                            <Tooltip title={isRTL ? `إصلاح: ${issue.fixActionType}` : `Fix: ${issue.fixActionType}`}>
                              <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); triggerFix(issue); }}><FixIcon /></IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={isRTL ? 'يحتاج تدخل يدوي' : 'Manual intervention'}>
                              <IconButton size="small" color="warning" onClick={(e) => { e.stopPropagation(); triggerFix(issue); }}><OpenIcon /></IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                              {loadingCascade && !cascade && <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>}
                              {cascade && renderCascadeContent(cascade)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    );
  };

  const renderCascadeContent = (cascade: CascadeAnalysis) => (
    <Stack spacing={1.5}>
      <Alert severity="warning" icon={<CascadeIcon />}>
        <Typography variant="subtitle2" fontWeight="bold">{isRTL ? '🔍 السبب الجذري' : 'Root Cause'}</Typography>
        <Typography variant="body2">{isRTL ? cascade.rootCauseDescriptionAr : cascade.rootCauseDescription}</Typography>
      </Alert>

      {cascade.rootNode && (
        <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '4px solid #f44336' }}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Chip label={`🎯 ${isRTL ? 'الكيان الجذر' : 'Root'}`} color="error" size="small" />
            <Chip label={cascade.rootNode.nodeType} size="small" />
            <Typography fontWeight="bold">{cascade.rootNode.documentNumber}</Typography>
            {cascade.rootNode.status && <Chip label={cascade.rootNode.status} size="small" color="info" />}
            {cascade.rootNode.entityId > 0 && <Button size="small" startIcon={<Visibility />} onClick={() => setDocModal({ open: true, type: cascade.rootNode!.nodeType, id: cascade.rootNode!.entityId })}>{isRTL ? 'عرض' : 'View'}</Button>}
            {cascade.rootNode.navigationPath && <Button size="small" startIcon={<OpenIcon />} onClick={() => window.open(cascade.rootNode!.navigationPath!, '_blank')}>{isRTL ? 'فتح' : 'Open'}</Button>}
          </Box>
          {cascade.rootNode.fixHintAr && <Alert severity="info" sx={{ mt: 1, fontSize: '0.8rem' }}>{isRTL ? cascade.rootNode.fixHintAr : cascade.rootNode.fixHint}</Alert>}
        </Paper>
      )}

      {cascade.affectedNodes && cascade.affectedNodes.length > 0 && (
        <Paper variant="outlined">
          <Box sx={{ p: 1, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
            <Typography variant="subtitle2" fontWeight="bold">{isRTL ? `🌳 المستندات المتأثرة (${cascade.affectedNodes.length})` : `Affected Documents (${cascade.affectedNodes.length})`}</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{isRTL ? 'النوع' : 'Type'}</TableCell>
                  <TableCell>{isRTL ? 'المستند' : 'Document'}</TableCell>
                  <TableCell>{isRTL ? 'الوصف' : 'Description'}</TableCell>
                  <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                  <TableCell align="right">{isRTL ? 'قبل' : 'Before'}</TableCell>
                  <TableCell align="right">{isRTL ? 'بعد' : 'After'}</TableCell>
                  <TableCell align="center">{isRTL ? 'إجراء' : 'Action'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cascade.affectedNodes.map((n, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Chip label={n.nodeType} size="small" /></TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{n.documentNumber}</TableCell>
                    <TableCell><Typography variant="caption">{isRTL ? (n.descriptionAr || n.description || '') : (n.description || '')}</Typography></TableCell>
                    <TableCell>{n.status && <Chip label={n.status} size="small" />}</TableCell>
                    <TableCell align="right">{fmt(n.amountBefore)}</TableCell>
                    <TableCell align="right" sx={{ color: '#2e7d32' }}>{fmt(n.amountAfter)}</TableCell>
                    <TableCell align="center">
                      {n.entityId > 0 && <Tooltip title={isRTL ? 'عرض المستند' : 'View'}><IconButton size="small" onClick={() => setDocModal({ open: true, type: n.nodeType, id: n.entityId })}><Visibility fontSize="small" /></IconButton></Tooltip>}
                      {n.navigationPath && <Tooltip title={isRTL ? 'فتح في النظام' : 'Open'}><IconButton size="small" onClick={() => window.open(n.navigationPath!, '_blank')}><OpenIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {cascade.recommendedSteps && cascade.recommendedSteps.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, borderLeft: '4px solid #4caf50' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{isRTL ? '🛠️ خطوات الإصلاح الموصى بها' : 'Recommended Fix Steps'}</Typography>
          <Stack spacing={1}>
            {cascade.recommendedSteps.map(step => (
              <Box key={step.stepNumber} display="flex" alignItems="center" gap={1.5}>
                <Avatar sx={{ bgcolor: step.executionMode === 'Auto' ? '#4caf50' : step.executionMode === 'Manual' ? '#ff9800' : '#2196f3', width: 28, height: 28, fontSize: 14 }}>{step.stepNumber}</Avatar>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="bold">{isRTL ? step.titleAr : step.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{isRTL ? step.descriptionAr : step.description}</Typography>
                </Box>
                <Chip label={isRTL ? (step.executionMode === 'Auto' ? 'آلي' : step.executionMode === 'Manual' ? 'يدوي' : 'مختلط') : step.executionMode} size="small" color={step.executionMode === 'Auto' ? 'success' : step.executionMode === 'Manual' ? 'warning' : 'info'} />
                {step.navigationPath && <Button size="small" variant="contained" onClick={() => navigate(step.navigationPath!)}>{isRTL ? 'انتقل' : 'Go'}</Button>}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Stack>
  );

  // ═════════ TAB 3: HISTORY ══════════════════════════════════════════
  const renderHistory = () => {
    if (loadingHistory) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (!history) return <Alert severity="info">{isRTL ? 'جارٍ التحميل...' : 'Loading...'}</Alert>;
    if (history.totalEntries === 0) return <Alert severity="info">{isRTL ? 'لا توجد سجلات إصلاح' : 'No fix records'}</Alert>;
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">{isRTL ? `📜 سجل الإصلاحات (${history.totalEntries})` : `Fix History (${history.totalEntries})`}</Typography>
          <Button startIcon={<RefreshIcon />} onClick={loadHistory}>{isRTL ? 'تحديث' : 'Refresh'}</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{isRTL ? 'الوقت' : 'When'}</TableCell>
                <TableCell>{isRTL ? 'النوع' : 'Type'}</TableCell>
                <TableCell>{isRTL ? 'العنوان' : 'Title'}</TableCell>
                <TableCell>{isRTL ? 'المستخدم' : 'User'}</TableCell>
                <TableCell align="right">{isRTL ? 'تغييرات' : 'Changes'}</TableCell>
                <TableCell align="right">{isRTL ? 'الأثر (KWD)' : 'Impact'}</TableCell>
                <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.entries.map(e => (
                <TableRow key={e.entryId} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(e.at, isRTL)}</TableCell>
                  <TableCell><Chip label={isRTL ? e.fixTypeAr : e.fixType} size="small" /></TableCell>
                  <TableCell>{isRTL ? e.titleAr : e.title}</TableCell>
                  <TableCell>{e.userName || '—'}</TableCell>
                  <TableCell align="right">{e.changeCount ?? '—'}</TableCell>
                  <TableCell align="right">{fmt(e.financialImpact)}</TableCell>
                  <TableCell><Chip label={e.status} size="small" color={e.status === 'Applied' ? 'success' : e.status === 'RolledBack' ? 'warning' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, background: 'linear-gradient(135deg, #232526 0%, #414345 100%)', color: 'white' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold">🏥 {isRTL ? 'مركز صحة المخزون — للسوبر أدمن' : 'Inventory Health Hub — Super Admin'}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>{isRTL ? 'كشف تلقائي للمشاكل + شجرة تأثير + خطوات إصلاح، في صفحة واحدة' : 'Auto-detect issues + impact tree + fix steps, in one page'}</Typography>
          </Box>
          <Chip label={isRTL ? 'الصلاحية: INVENTORY_HEALTH_HUB · الإدارة من صفحة المستخدمين' : 'Permission: INVENTORY_HEALTH_HUB · Manage via Users page'} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white' }} />
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3, mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<DashboardIcon />} label={isRTL ? 'لوحة الصحة' : 'Overview'} iconPosition="start" />
          <Tab icon={<BugIcon />} label={isRTL ? `متعقّب المشاكل${issues ? ` (${issues.totalIssues})` : ''}` : `Issue Tracker${issues ? ` (${issues.totalIssues})` : ''}`} iconPosition="start" />
          <Tab icon={<HistoryIcon />} label={isRTL ? 'سجل الإصلاحات' : 'Fix History'} iconPosition="start" />
        </Tabs>
      </Paper>

      <Box>
        {tab === 0 && renderOverview()}
        {tab === 1 && renderIssueTracker()}
        {tab === 2 && renderHistory()}
      </Box>

      <OrphanBatchFixDialog open={orphanOpen} onClose={() => setOrphanOpen(false)} isRTL={isRTL} onFixComplete={() => { loadOverview(); loadIssues(); }} />
      <FifoFixDialog open={fifoOpen} onClose={() => setFifoOpen(false)} isRTL={isRTL} onFixComplete={() => { loadOverview(); loadIssues(); }} />
      <DocumentPreviewModal open={docModal.open} documentType={docModal.type} entityId={docModal.id} onClose={() => setDocModal({ open: false, type: null, id: null })} isRTL={isRTL} />
    </Box>
  );
};

export default InventoryHealthHubPage;
